'use client';

import { loginAction, resendVerificationEmailAction } from '@/app/lib/actions/auth';
import { useUser } from "@/app/contexts/user-context";
import { SlideUp } from '@/components/slide-up';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { loginSchema, type LoginInput } from '@/schemas/auth';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRightIcon, Eye, EyeOff, Loader2 } from 'lucide-react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState, useTransition } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import ResendEmailAlert from './resend-email-alert';

export function LoginForm() {
  const [isPending, startTransition] = useTransition();
  const router = useRouter();
  const searchParams = useSearchParams();
  const needsVerification = searchParams.get('verification') === 'required';
  const { setUser } = useUser();

  const [showPassword, setShowPassword] = useState(false);
  const [showResendButton, setShowResendButton] = useState(needsVerification);
  const [resendLoading] = useState(false);

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onBlur',
  });

  async function onSubmit(data: LoginInput) {
    startTransition(async () => {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        formData.append(key, value as string);
      });

      const result = await loginAction(formData);

      if (result.success) {
        if(result.user){
          setUser(result.user);
        }
        router.push(result.redirectTo || '/dashboard');
        return;
      }

      if (result.needVerification) {
        setShowResendButton(true);
        form.setError('root', {
          message: result.message || 'Please verify your email before logging in',
        });
        return;
      }

      if (result.validation) {
        Object.entries(result.validation).forEach(([field, errors]) => {
          form.setError(field as keyof LoginInput, {
            message: (errors as string[])[0],
          });
        });
      } else if (result.error) {
        form.setError('root', {
          message:
            result.error === 'Server error'
              ? "We're experiencing technical difficulties. Please try again later."
              : result.error,
        });
      }
    });
  }

  const handleResendVerification = async () => {
    const email = form.getValues('email');
    if (!email) {
      form.setError('email', { message: 'Email is required' });
      return;
    }

    startTransition(async () => {
      const result = await resendVerificationEmailAction(email);
      if (result?.success) {
        toast.success(`Verification email has been sent to ${email}`);
      } else {
        form.setError('root', {
          message: result.error || 'Failed to send verification email',
        });
      }
    });
  };

  return (
    <SlideUp>
      <div className="mx-auto w-full max-w-md space-y-4">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold tracking-tight">Login to your account</h2>
          <p className="text-sm text-gray-600">Let the rhythm guide you.</p>
        </div>
        {showResendButton && (
          <ResendEmailAlert resendLoading={resendLoading} handleResendVerification={handleResendVerification} />
        )}
        <Accordion
          type="single"
          collapsible
          className="w-full rounded-md border border-primary bg-primary/10 px-2 text-primary"
        >
          <AccordionItem className="border-none" value="item-1">
            <AccordionTrigger className="py-2 font-semibold">
              Returning customer? Click here for important information
            </AccordionTrigger>
            <AccordionContent className="space-y-1.5 leading-relaxed">
              <p>
                We&apos;ve recently upgraded our system. If you had an account with us before, use the &quot;Reset
                Password&quot; option below
              </p>
              <p>This will automatically verify your email address and set a new password</p>
              <Link href={'/forgot-password'} className="block">
                <Button variant={'outline'}>
                  Reset password <ArrowRightIcon />
                </Button>
              </Link>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input type={showPassword ? 'text' : 'password'} className="pr-10" {...field} />
                    </FormControl>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <FormMessage />
                  <Link href="/forgot-password" className="mt-2 block text-right text-sm text-primary hover:underline">
                    Forgot password?
                  </Link>
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>
        </Form>

        <p className="mt-6 text-center text-[#4b4b4b]">
          Don&apos;t have an account?{' '}
          <Link href="/register" className="text-primary hover:underline">
            Register
          </Link>
        </p>
      </div>
    </SlideUp>
  );
}
